import React from "react";
import { Box, Typography, Paper, Chip } from "@mui/material";
import { Bar } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

const ProductPerformanceChart = ({ productPerformance = [], isLoading = false }) => {
    if (isLoading) {
        return (
            <Paper sx={{ p: 3, height: 400 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Top Products Performance
                </Typography>
                <Box
                    sx={{
                        height: 300,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        Loading...
                    </Typography>
                </Box>
            </Paper>
        );
    }

    // Take top 8 products for better visualization
    const topProducts = productPerformance.slice(0, 8);

    const chartData = {
        labels: topProducts.map((product) => 
            product.name.length > 15 
                ? product.name.substring(0, 15) + "..." 
                : product.name
        ),
        datasets: [
            {
                label: "Orders",
                data: topProducts.map((product) => product.orders),
                backgroundColor: "rgba(237, 32, 36, 0.8)",
                borderColor: "#ED2024",
                borderWidth: 1,
                borderRadius: 4,
                borderSkipped: false,
            },
            {
                label: "Revenue (₹)",
                data: topProducts.map((product) => parseFloat(product.revenue)),
                backgroundColor: "rgba(76, 175, 80, 0.8)",
                borderColor: "#4CAF50",
                borderWidth: 1,
                borderRadius: 4,
                borderSkipped: false,
                yAxisID: "y1",
            },
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: "top",
                labels: {
                    usePointStyle: true,
                    padding: 20,
                    font: {
                        size: 12,
                        weight: "500",
                    },
                },
            },
            tooltip: {
                mode: "index",
                intersect: false,
                backgroundColor: "rgba(0, 0, 0, 0.8)",
                titleColor: "#ffffff",
                bodyColor: "#ffffff",
                borderColor: "#ED2024",
                borderWidth: 1,
                cornerRadius: 8,
                displayColors: true,
                callbacks: {
                    label: function (context) {
                        if (context.datasetIndex === 1) {
                            return `Revenue: ₹${context.parsed.y.toLocaleString()}`;
                        }
                        return `Orders: ${context.parsed.y}`;
                    },
                    afterLabel: function (context) {
                        if (context.datasetIndex === 0) {
                            const product = topProducts[context.dataIndex];
                            return `Product: ${product.name}\nPrice: ₹${product.price}`;
                        }
                        return "";
                    },
                },
            },
        },
        scales: {
            x: {
                display: true,
                title: {
                    display: true,
                    text: "Products",
                    font: {
                        size: 12,
                        weight: "600",
                    },
                },
                grid: {
                    display: false,
                },
                ticks: {
                    font: {
                        size: 10,
                    },
                    maxRotation: 45,
                    minRotation: 0,
                },
            },
            y: {
                type: "linear",
                display: true,
                position: "left",
                title: {
                    display: true,
                    text: "Orders",
                    font: {
                        size: 12,
                        weight: "600",
                    },
                },
                grid: {
                    color: "rgba(0, 0, 0, 0.1)",
                },
                ticks: {
                    font: {
                        size: 11,
                    },
                },
            },
            y1: {
                type: "linear",
                display: true,
                position: "right",
                title: {
                    display: true,
                    text: "Revenue (₹)",
                    font: {
                        size: 12,
                        weight: "600",
                    },
                },
                grid: {
                    drawOnChartArea: false,
                },
                ticks: {
                    font: {
                        size: 11,
                    },
                    callback: function (value) {
                        return `₹${value.toLocaleString()}`;
                    },
                },
            },
        },
        interaction: {
            mode: "nearest",
            axis: "x",
            intersect: false,
        },
    };

    return (
        <Paper sx={{ p: 3, height: 450 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Top Products Performance
            </Typography>
            
            {/* Product Summary */}
            <Box sx={{ mb: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
                {topProducts.slice(0, 4).map((product, index) => (
                    <Chip
                        key={product.id}
                        label={`${product.name.substring(0, 20)}${product.name.length > 20 ? '...' : ''}`}
                        size="small"
                        color={index === 0 ? "primary" : "default"}
                        variant={index === 0 ? "filled" : "outlined"}
                        sx={{ fontSize: "0.75rem" }}
                    />
                ))}
            </Box>

            <Box sx={{ height: 300, position: "relative" }}>
                <Bar data={chartData} options={options} />
            </Box>
        </Paper>
    );
};

export default ProductPerformanceChart;
